import { useRef, useState, useEffect, useContext, useLayoutEffect, useMemo } from 'react'
import {
  CommandBarButton,
  IconButton,
  Dialog,
  DialogType,
  Stack,
  Dropdown,
  DefaultButton,
  ContextualMenu,
  StackItem,
  Text,
  IStackStyles,
  ICommandBarStyles,
  IContextualMenuItem,
  DialogFooter,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  TooltipHost,
  IDropdownStyles,
  Slider,
  ISliderStyles
} from '@fluentui/react'
import { SquareRegular, ShieldLockRegular, ErrorCircleRegular } from '@fluentui/react-icons'

//import '@fontsource/montserrat'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import uuid from 'react-uuid'
import { isEmpty } from 'lodash'
import DOMPurify from 'dompurify'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'

import styles from './Chat.module.css'
import ETF from '../../assets/logo.png'
import SpeechBubble from '../../assets/SpeechBubble.png'
import WizardHat from '../../assets/WizardHat.png'
import Network from '../../assets/Network.png'
import Web from '../../assets/Web.png'

import { XSSAllowTags } from '../../constants/sanatizeAllowables'

import {
  ChatMessage,
  ConversationRequest,
  conversationApi,
  wizardApi,
  Citation,
  ToolMessageContent,
  AzureSqlServerExecResults,
  ChatResponse,
  getUserInfo,
  Conversation,
  historyGenerate,
  historyUpdate,
  historyClear,
  ChatHistoryLoadingState,
  CosmosDBStatus,
  ErrorMessage,
  ExecResults,
  historyDeleteAll,
  FieldItem,
  checkCache,
  pollWizardResult
} from '../../api'
import { Answer } from '../../components/Answer'
import { QuestionInput } from '../../components/QuestionInput'
import { WizardInput } from '../../components/WizardInput'
import { AppStateContext } from '../../state/AppProvider'
import { useBoolean } from '@fluentui/react-hooks'
import ChatHistoryList from '../../components/ChatHistory/ChatHistoryList'
import React from 'react'

import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { useParams, useOutletContext } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { MessageCircle, WandSparkles, Globe, Share2, Flag, Goal, File } from 'lucide-react'
import { AppDropdown } from '@/components/app-dropdown'

// Initialize APP Insights
var reactPlugin = new ReactPlugin()
var appInsights = new ApplicationInsights({
  config: {
    connectionString:
      'InstrumentationKey=0ea31e02-5e14-46bd-9b3a-df69080f2934;IngestionEndpoint=https://francecentral-1.in.applicationinsights.azure.com/;LiveEndpoint=https://francecentral.livediagnostics.monitor.azure.com/;ApplicationId=2da698ac-515f-4320-a369-3be892df2d62',
    enableAutoRouteTracking: true,
    extensions: [reactPlugin]
  }
})
appInsights.loadAppInsights()

const enum messageStatus {
  NotRunning = 'Not Running',
  Processing = 'Processing',
  Done = 'Done'
}

type OutletContextType = {
  docWizard: boolean
  setDocWizard: React.Dispatch<React.SetStateAction<boolean>>
}

const Chat = () => {
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui
  const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingWizard, setIsLoadingWizard] = useState<boolean>(false)
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false)
  const [activeCitation, setActiveCitation] = useState<Citation>()
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const abortFuncs = useRef([] as AbortController[])
  const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [execResults, setExecResults] = useState<ExecResults[]>([])
  const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning)
  const [clearingChat, setClearingChat] = useState<boolean>(false)
  const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true)
  const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>()
  const [logo, setLogo] = useState('')
  const [answerId, setAnswerId] = useState<string>('')
  const environmentVariablesList = appStateContext?.state.environmentVariablesList
  const indexesList = appStateContext?.state.indexesList
  const templatesList = appStateContext?.state.templatesList
  const countryList = appStateContext?.state.countryList
  const countryprompt = appStateContext?.state.countryprompt
  const yearlist = appStateContext?.state.yearlist
  const yearprompt = appStateContext?.state.yearprompt
  const [templateOptions, setTemplateOptions] = useState<{ key: string; text: string }[]>([])
  const [templateValue, setTemplateValue] = useState<string>('Choose a template')
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({})
  const [selectedTemplateFields, setSelectedTemplateFields] = useState<FieldItem[]>([])
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [selectedLowerYear, setSelectedLowerYear] = useState<number>(new Date().getFullYear() - 15)
  const [filterByYear, setFilterByYear] = useState<boolean>(false)
  const { conversation_id } = useParams() // Get the conversation ID from the URL
  const [startYear, setStartYear] = useState(-1)
  const [endYear, setEndYear] = useState(-1)
  const [yearPrompt_fe, setYearPrompt] = useState('')
  const [countryPrompt_fe, setCountryPrompt] = useState('')
  // Started adding here

  const templateIcons: Record<string, any> = {
    'Regional Fiche': Goal,
    'Country Fiche': Flag
  }

  const isAllFieldsSelected = useMemo(() => {
    return selectedTemplateFields
      .filter(field => field.type === 'dropdown') // Validate only dropdown fields
      .every(field => fieldValues[field.name])
  }, [templateValue, selectedTemplateFields, fieldValues])

  const isCancelledRef = useRef(false)

  const [showSwitchingPageDialog, setShowSwitchingPageDialog] = useState(false)
  const handleOpenSwitchingPageDialog = () => setShowSwitchingPageDialog(true)
  const handleCloseSwitchingPageDialog = () => setShowSwitchingPageDialog(false)

  const [showWizardWarningDialog, setShowWizardWarningDialog] = useState(false)

  const handleOpenWizardWarningDialog = () => {
    setShowWizardWarningDialog(true)
  }
  const handleCloseWizardWarningDialog = () => setShowWizardWarningDialog(false)

  const dropdownStyles: Partial<IDropdownStyles> = {
    root: {
      width: '250px' // Set the width of the dropdown to 250px
    },
    // Targets the input part of the dropdown (where the user selects)
    dropdown: {
      border: '1px solid #ccc', // Light gray border for the input field
      borderRadius: '10px', // Rounded corners for the input field
      padding: '0 8px' // Optional: Adjust padding as needed
    },
    // Optional: Apply to the expanded dropdown list when the options are shown
    callout: {
      borderRadius: '10px', // Rounded corners for the dropdown list
      border: 'none' // Ensure no border for the expanded list
    },
    caretDown: {
      color: '#df006e' // Set chevron color to pink
    },
    label: {
      fontSize: '15px',
      fontWeight: '700',
      color: 'gray'
    },
    title: {
      border: 'none', // Remove any border on the title (if it’s being applied)
      backgroundColor: 'none'
    },
    dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
    dropdownItem: { height: 'auto' }
  }

  const sliderStyles: Partial<ISliderStyles> = {
    root: {
      width: '100%', // Match dropdown width
      margin: '0px 0' // Add spacing if needed
    },
    titleLabel: {
      fontSize: '0.875rem',
      fontWeight: '400',
      color: '#6b7280',
      marginBottom: '19px',
      lineHeight: '1.25rem',
      paddingLeft: '15px',
      paddingTop: '1px'
    },
    container: {
      // Container around the slider track
      padding: '0 8px'
    },
    line: {
      height: '4px',
      borderRadius: '10px',
      backgroundColor: '#ccc' // Track color
    },
    thumb: {
      width: '14px',
      height: '14px',
      backgroundColor: '#009CDA', // Pink thumb like caretDown
      border: 'none'
    },
    activeSection: {
      backgroundColor: '#009CDA' // Active part of track
    },
    inactiveSection: {
      backgroundColor: '#eee' // Inactive track
    },
    valueLabel: {
      color: 'gray',
      fontWeight: 'bold',
      fontSize: '13px'
    }
  }

  const { dispatch } = appStateContext!

  useEffect(() => {
    
    const fetchConversation = async () => {
      if (!conversation_id) return // Don't fetch if no conversation ID
      try {
        const response = await fetch('/history/read', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ conversation_id })
        })
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to fetch conversation.')
        }
        const data = await response.json()
        setMessages(data.messages)
      } catch (err: any) {
        console.error('Error fetching conversation:', err.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchConversation()
  }, [conversation_id])

  useEffect(() => {
    
    fetch('/auth/user')
      .then(response => response.json())
      .then(data => {
        const userId = data.userId
        appInsights.context.user.clearAuthenticatedUserContext()
        appInsights.context.user.id = userId
        appInsights.setAuthenticatedUserContext(userId)
      })
  }, [])

  // Get indexes names from Indexes List and change the dropdown source options

  const [sourceOptions, setSourceOptions] = useState<{ key: string; text: string }[]>([])

  useEffect(() => {
    
    if (indexesList?.indexes_list) {
      const newOptions = indexesList.indexes_list.map(value => ({
        key: value.key,
        text: value.text
      }))
      setSourceOptions(newOptions)
    }
  }, [indexesList])

  //HS
  const [countryOptions, setCountryOptions] = useState<{ key: string; text: string }[]>([])

  useEffect(() => {
    
    if (Array.isArray(countryList)) {
      const newOptions = countryList.map(value => ({
        key: value,
        text: value
      }))
      setCountryOptions(newOptions)
    }
  }, [countryList])

  useEffect(() => {
    
    if (yearlist != undefined && yearlist.length === 8) {
      setStartYear(parseInt(yearlist.slice(0, 4)))
      setEndYear(parseInt(yearlist.slice(4)))
      setSelectedLowerYear(parseInt(yearlist.slice(0, 4)))
      setSelectedYear(parseInt(yearlist.slice(4)))
    } else {
    }
  }, [yearlist])

  useEffect(() => {
    
    if (yearprompt != undefined) {
      setYearPrompt(yearprompt)
    } else {
    }
  }, [yearprompt])

  useEffect(() => {
    
    if (countryprompt != undefined) {
      setCountryPrompt(countryprompt)
    } else {
    }
  }, [countryprompt])

  useEffect(() => {
    
    if (templatesList?.templates_list) {
      const options = templatesList.templates_list.map(template => ({
        key: template.name,
        text: template.name
      }))
      setTemplateOptions(options)
    }
  }, [templatesList])

  const [showCopyButton, setShowCopyButton] = useState<boolean>(false)

  const callbackRef = useRef<Function>()
  const handleOnRef = (callback: Function) => {
    callbackRef.current = callback
    setShowCopyButton(true)
  }

  // Side Panel
  // State to check if Panel is open/close
  const [isSideBarPanelOpen, setisSideBarPanelOpen] = useState<boolean>(true)

  // Tabs
  // State to check which tabs are open
  const [isTab1Open, setIsTab1Open] = useState<boolean>(true)
  const [isTab2Open, setIsTab2Open] = useState<boolean>(false)
  const [isTab3Open, setIsTab3Open] = useState<boolean>(false)

  // State to store text color (so when a tab is open the text is blue)
  const [textColorTab1, setTextColorTab1] = useState('white')
  const [textColorTab3, setTextColorTab3] = useState('gray')

  const [textColorWebSearch, setTextColorWebSearch] = useState('gray')
  const [textColorTrustedWebSearch, setTextColorTrustedWebSearch] = useState('gray')

  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)

  const clickWebSearch = () => {
    if (searchWeb) {
      setSearchTrustedWeb(false)
      setSearchWeb(false)
      /* setTextColorWebSearch('gray')
      setTextColorTrustedWebSearch('gray') */
    } else {
      setSearchTrustedWeb(false)
      setSearchWeb(true)
      /* setTextColorWebSearch('#df006e')
      setTextColorTrustedWebSearch('gray') */
    }
  }

  const clickTrustedWebSearch = () => {
    if (searchTrustedWeb) {
      setSearchTrustedWeb(false)
      setSearchWeb(false)
      /* setTextColorWebSearch('gray')
      setTextColorTrustedWebSearch('gray') */
    } else {
      setSearchTrustedWeb(true)
      setSearchWeb(false)
      /* setTextColorWebSearch('gray')
      setTextColorTrustedWebSearch('#df006e') */
    }
  }

  const switchToTab1 = async () => {
    // Check if there's an active request to abort
    if (currentTaskId) {
      stopGeneratingWizard()
    }
    const lastAbortController = abortFuncs.current[0]
    if (lastAbortController) {
      lastAbortController.abort() // Cancel the request
    }

    setTimeout(() => {
      if (!abortFuncs.current[0]) {
        newChat()
      }
    }, 500) // Small delay to ensure the request is stopped

    setTextColorTab1('white')
    setTextColorTab3('gray')
    setIsTab1Open(true)
    setIsTab2Open(false)
    setIsTab3Open(false)
    setDocWizard(false)
    setFieldValues({})
    setTemplateValue('')

    if (showSwitchingPageDialog) {
      handleCloseSwitchingPageDialog()
    }
  }

  const clickTab1 = () => {
    if (docWizard && appStateContext?.state?.currentChat?.messages && messages.length >= 1) {
      handleOpenSwitchingPageDialog()
    } else {
      switchToTab1()
    }
  }

  const actionRef = useRef(switchToTab1)

  useEffect(() => {
    actionRef.current = switchToTab1
  }, [switchToTab1])

  useEffect(() => {
    dispatch({
      type: 'SET_LAYOUT_BUTTON_ACTION',
      payload: () => actionRef.current?.()
    })

    return () => {
      dispatch({ type: 'SET_LAYOUT_BUTTON_ACTION', payload: undefined })
    }
  }, [dispatch])

  const clickTab2 = () => {
    if (!docWizard && appStateContext?.state?.currentChat?.messages && messages.length >= 1) {
      newChat()
    }
    setTextColorTab3('white')
    setTextColorTab1('gray')
    setIsTab3Open(true)
    setIsTab1Open(false)
    setIsTab2Open(false)

    setSearchWeb(false)
    setSearchTrustedWeb(false)
    setDocWizard(true)
  }

  // End of Tabs
  // States for what used to be environment variables - now they get the default values from it but are changed according to dropdowns

  const [tokenValue, setTokenValue] = React.useState(environmentVariablesList?.environment_variables?.tokens || 500)

  const [temperatureValue, setTemperatureValue] = React.useState(
    environmentVariablesList?.environment_variables?.temperature || 0.5
  )

  const [sysMessage, setSysMessage] = React.useState(
    environmentVariablesList?.environment_variables?.sys_message ||
      "Your task is to assist ETF employees with finding information on human resources themes from publicly accessible documents. Additionally, perform language-related tasks such as translation, paraphrasing, and summarization. You have access to a repository of publicly accessible documents related to human resources themes within ETF. You should greet the users only when they greet you, by saying the following: ‘Hello! How can I assist you today? Whether you're looking for information among ETF's HR documents or need help with translation, paraphrasing, summarization, or any other language-related task, feel free to ask me.’.  If you don't know how to answer to the user you have to answer the following: ‘I apologize, but it seems I couldn't find the information or perform the requested language-related task in the publicly accessible documents. For urgent matters or further assistance, please open a Compass ticket at: https://etfcompass.service-now.com/sp‘.  Some examples of what users may ask you are: 1. ‘What is ETF?’; 2. ‘Translate the ETF's diversity and inclusion policy into French.’; 3. ‘Paraphrase the section on performance evaluations from the human resources guidelines.’; 4. ‘Summarize the benefits package for ETF employees.’.  Your output format should provide clear and concise responses in paragraph format. Some additional instructions you should follow are to ensure that the information is up-to-date, if the document is too long, summarize the relevant details, if the information is not present in the documents, indicate that clearly, make use of a professional and informative tone in responses, if there are multiple relevant documents, prioritize based on relevance."
  )

  //country Dropdown HS
  const [countryValueText, setCountryValueText] = useState<string>('All Countries')
  const [countryValue, setCountryValue] = useState<string>('All Countries')
  const onCountryChange = (selectedOption: any) => {
    setCountryValue(selectedOption.key)
    setCountryValueText(selectedOption.text)
  }

  // Source Dropdown
  //here is important HS
  const [sourceValueText, setSourceValueText] = useState<string>('General')
  const [sourceValue, setSourceValue] = useState<string>(
    environmentVariablesList?.environment_variables?.source || 'prd-etf-gpt-general-index'
  )

  const onSourceChange = (selectedOption: any) => {
    setSourceValue(selectedOption.key)
    setSourceValueText(selectedOption.text)

    /* if (appStateContext?.state?.currentChat?.messages && messages.length >= 1) {
      newChat()
    } */
  }

  // End of Source Dropdown

  // Templates Dropdown

  const onTemplateValueChange = (selectedOption: any) => {
    setTemplateValue(selectedOption.key)

    // Find selected template fields
    const selectedTemplate = templatesList?.templates_list?.find(template => template.name === selectedOption.key)
    setSelectedTemplateFields(selectedTemplate?.fields || [])

    // Reset field values
    setFieldValues({})
  }

  // Handle field selection
  const onFieldValueChange = (fieldName: string, selectedOption: any) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: selectedOption.key
    }))
  }

  // Region Dropdown

  /* const [regionValue, setRegionValue] = useState<string>('Choose a region')

  const onRegionValueChange = (selectedOption: any) => {
    setRegionValue(selectedOption.key)
  }


  const regionOptions = [
    { key: 'South Eastern Europe and Turkey (SEET)', text: 'South Eastern Europe and Turkey (SEET)' },
    { key: 'Southern and Eastern Mediterranean (SEMED)', text: 'Southern and Eastern Mediterranean (SEMED)' },
    { key: 'Eastern Europe (EE)', text: 'Eastern Europe (EE)' },
    { key: 'Central Asia (CA)', text: 'Central Asia (CA)' }
  ] */

  const answerTypeOptions = [
    { key: 'Short', text: 'Short' },
    { key: 'Regular', text: 'Regular' },
    { key: 'Verbose', text: 'Verbose' }
  ]

  // End of Answer type Dropdown

  // Web Search Switch

  const [searchWeb, setSearchWeb] = React.useState(false)
  const onChangeSearchWeb = React.useCallback(
    (ev: any) => {
      setSearchWeb(ev.currentTarget.checked)
    },
    [setSearchWeb]
  )

  // Web Search Switch

  const [searchTrustedWeb, setSearchTrustedWeb] = React.useState(false)
  const onChangeSearchTrustedWeb = React.useCallback(
    (ev: any) => {
      setSearchTrustedWeb(ev.currentTarget.checked)
    },
    [setSearchTrustedWeb]
  )

  // Doc Wizard Switch

  const { docWizard, setDocWizard } = useOutletContext<OutletContextType>() /*React.useState(false)*/
  const commandBarButtonStyle: Partial<IStackStyles> = {
    root: { height: '50px' }
  }

  const commandBarStyle: ICommandBarStyles = {
    root: {
      padding: '0',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      color: '#df006e'
    }
  }

  const [showContextualMenu, setShowContextualMenu] = React.useState(false)
  const [hideClearAllDialog, { toggle: toggleClearAllDialog }] = useBoolean(true)
  const [hideSwitchPageDialog, { toggle: toggleSwitchPageDialog }] = useBoolean(true)
  const [clearing, setClearing] = React.useState(false)
  const [clearingError, setClearingError] = React.useState(false)

  const switchPageDialogContentProps = {
    type: DialogType.close,
    title: 'Are you sure you want to exit the Document Wizard?',
    closeButtonAriaLabel: 'Close',
    subText: 'All progress will be lost.'
  }

  const [showWizardCacheDialog, setshowWizardCacheDialog] = useState(false)
  const [WizardCacheDialogMessage, setshowWizardCacheDialogMessage] = useState<string>('')
  const [showWizardNoCacheDialog, setshowWizardNoCacheDialog] = useState(false)

  const handleCloseWizardCacheDialog = () => setshowWizardCacheDialog(false)
  const handleCloseWizardNoCacheDialog = () => setshowWizardNoCacheDialog(false)
  const switchPageDialogContentPropsWizardCache = {
    type: DialogType.close,
    title: WizardCacheDialogMessage,
    closeButtonAriaLabel: 'Close',
    subText: 'Generating a new draft will take a few minutes.'
  }

  const switchPageDialogContentPropsWizardNoCache = {
    type: DialogType.close,
    title: 'There is no saved draft. This process will take a few minutes.',
    closeButtonAriaLabel: 'Close',
    subText: ''
  }

  const wizardWarningProps = {
    type: DialogType.close,
    title: 'This process might take a few minutes. Are you sure you want to proceed?',
    closeButtonAriaLabel: 'Close'
  }

  const clearAllDialogContentProps = {
    type: DialogType.close,
    title: !clearingError ? 'Are you sure you want to clear all chat history?' : 'Error deleting all of chat history',
    closeButtonAriaLabel: 'Close',
    subText: !clearingError
      ? 'All chat history will be permanently removed.'
      : 'Please try again. If the problem persists, please contact the site administrator.'
  }

  const menuItems: IContextualMenuItem[] = [
    {
      key: 'clearAll',
      text: 'Clear all chat history',
      iconProps: { iconName: 'Delete' }
    }
  ]

  const handleHistoryClick = () => {
    appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
  }

  const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault() // don't navigate
    setShowContextualMenu(true)
  }, [])

  const onHideContextualMenu = React.useCallback(() => setShowContextualMenu(false), [])

  const onClearAllChatHistory = async () => {
    setClearing(true)
    let response = await historyDeleteAll()
    if (!response!.ok) {
      setClearingError(true)
    } else {
      appStateContext?.dispatch({ type: 'DELETE_CHAT_HISTORY' })
      toggleClearAllDialog()
    }
    setClearing(false)
  }

  const onHideClearAllDialog = () => {
    toggleClearAllDialog()
    setTimeout(() => {
      setClearingError(false)
    }, 2000)
  }

  // Stopped adding here

  const errorDialogContentProps = {
    type: DialogType.close,
    title: errorMsg?.title,
    closeButtonAriaLabel: 'Close',
    subText: errorMsg?.subtitle
  }

  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  }
  const modalPropsWizard = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { minWidth: 700 } }
  }
  const modalPropsWizardNoCache = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { minWidth: 450 } }
  }
  const [ASSISTANT, TOOL, ERROR] = ['assistant', 'tool', 'error']
  const NO_CONTENT_ERROR = 'No content in messages object.'

  useEffect(() => {
    
    if (messages.length === 0) {
      appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    }
  }, [messages])

  useEffect(() => {
    
    if (
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working &&
      appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured &&
      appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
      hideErrorDialog
    ) {
      let subtitle = `${appStateContext.state.isCosmosDBAvailable.status}. Please contact the site administrator.`
      setErrorMsg({
        title: 'Chat history is not enabled',
        subtitle: subtitle
      })
      toggleErrorDialog()
    }
  }, [appStateContext?.state.isCosmosDBAvailable])

  const handleErrorDialogClose = () => {
    toggleErrorDialog()
    setTimeout(() => {
      setErrorMsg(null)
    }, 500)
  }

  useEffect(() => {
    
    if (!appStateContext?.state.isLoading) {
      setLogo(ui?.chat_logo || ui?.logo || ETF)
    }
  }, [appStateContext?.state.isLoading])

  useEffect(() => {
    
    setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
  }, [appStateContext?.state.chatHistoryLoadingState])

/*    const getUserInfoList = async () => {
    if (!AUTH_ENABLED) {
      setShowAuthMessage(false)
      return
    }
    const userInfoList = await getUserInfo()
    if (userInfoList.length === 0 && window.location.hostname !== '127.0.0.1') {
      setShowAuthMessage(true)
    } else {
      setShowAuthMessage(false)
    }
  }  */

  let assistantMessage = {} as ChatMessage
  let toolMessage = {} as ChatMessage
  let assistantContent = ''

  useEffect(() => parseExecResults(execResults), [execResults])

  const parseExecResults = (exec_results_: any): void => {
    if (exec_results_ == undefined) return
    const exec_results = exec_results_.length === 2 ? exec_results_ : exec_results_.splice(2)
    appStateContext?.dispatch({
      type: 'SET_ANSWER_EXEC_RESULT',
      payload: { answerId: answerId, exec_result: exec_results }
    })
  }

  const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
    if (typeof resultMessage.content === 'string' && resultMessage.content.includes('all_exec_results')) {
      const parsedExecResults = JSON.parse(resultMessage.content) as AzureSqlServerExecResults
      setExecResults(parsedExecResults.all_exec_results)
      assistantMessage.context = JSON.stringify({
        all_exec_results: parsedExecResults.all_exec_results
      })
    }

    if (resultMessage.role === ASSISTANT) {
      setAnswerId(resultMessage.id)
      assistantContent += resultMessage.content
      assistantMessage = { ...assistantMessage, ...resultMessage }
      assistantMessage.content = assistantContent

      if (resultMessage.context) {
        toolMessage = {
          id: uuid(),
          role: TOOL,
          content: resultMessage.context,
          date: new Date().toISOString()
        }
      }
    }

    if (resultMessage.role === TOOL) toolMessage = resultMessage

    if (!conversationId) {
      isEmpty(toolMessage)
        ? setMessages([...messages, userMessage, assistantMessage])
        : setMessages([...messages, userMessage, toolMessage, assistantMessage])
    } else {
      isEmpty(toolMessage)
        ? setMessages([...messages, assistantMessage])
        : setMessages([...messages, toolMessage, assistantMessage])
    }
  }

  const makeApiRequestWithoutCosmosDB = async (
    question: ChatMessage['content'],
    source: string,
    sys_message: string,
    tokens: number,
    temperature: number,
    search_web: boolean,
    search_trusted_web: boolean,
    doc_wizard: boolean,
    template: string,
    first_draft: boolean,
    context?: string,
    conversationId?: string,
    countryValue?: string
  ) => {
    // Hide copy button until the answer is fully shown
    setShowCopyButton(false)
    setIsLoading(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const questionContent =
      typeof question === 'string'
        ? countryValue === 'All Countries'
          ? question
          : question + ' Consider only ' + countryValue
        : [
            { type: 'text', text: question[0].text },
            { type: 'image_url', image_url: { url: question[1].image_url.url } }
          ]
    question = typeof question !== 'string' && question[0]?.text?.length > 0 ? question[0].text : question

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: questionContent as string,
      context: context,
      source: source,
      sys_message: sys_message,
      tokens: tokens,
      temperature: temperature,
      search_web: search_web,
      search_trusted_web: search_trusted_web,
      doc_wizard: doc_wizard,
      template: template,
      first_draft: first_draft,
      date: new Date().toISOString()
    }

    let conversation: Conversation | null | undefined
    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question as string,
        messages: [userMessage],
        date: new Date().toISOString()
      }
    } else {
      conversation = appStateContext?.state?.currentChat
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoading(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
      }
    }

    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
    setMessages(conversation.messages)

    const request: ConversationRequest = {
      messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
    }

    let result = {} as ChatResponse
    try {
      const response = await conversationApi(request, abortController.signal)
      if (response?.body) {
        const reader = response.body.getReader()

        let runningText = ''
        while (true) {
          setProcessMessages(messageStatus.Processing)
          const { done, value } = await reader.read()
          if (done) break

          var text = new TextDecoder('utf-8').decode(value)
          const objects = text.split('\n')
          objects.forEach(obj => {
            try {
              if (obj !== '' && obj !== '{}') {
                runningText += obj
                result = JSON.parse(runningText)
                if (result.choices?.length > 0) {
                  result.choices[0].messages.forEach(msg => {
                    msg.id = result.id
                    msg.date = new Date().toISOString()
                  })
                  if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                    setShowLoadingMessage(false)
                  }
                  result.choices[0].messages.forEach(resultObj => {
                    processResultMessage(resultObj, userMessage, conversationId)
                  })
                } else if (result.error) {
                  throw Error(result.error)
                }
                runningText = ''
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e)
                throw e
              } else {
                console.log('Incomplete message. Continuing...')
              }
            }
          })
        }
        conversation.messages.push(toolMessage, assistantMessage)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        setMessages([...messages, toolMessage, assistantMessage])
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage =
          'An error occurred. Please try again. If the problem persists, please contact the site administrator.'
        if (result.error?.message) {
          errorMessage = result.error.message
        } else if (typeof result.error === 'string') {
          errorMessage = result.error
        }

        errorMessage = parseErrorMessage(errorMessage)

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString()
        }
        conversation.messages.push(errorChatMsg)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        setMessages([...messages, errorChatMsg])
      } else {
        setMessages([...messages, userMessage])
      }
    } finally {
      // Show Copy button
      setIsLoading(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
      callbackRef?.current?.(true)
    }

    return abortController.abort()
  }

  const showDialogDocumentWizard = async () => {
    stopGeneratingWizard()
    //if (currentTaskId) {
    //  stopGeneratingWizard()
    //}
    const lastAbortController = abortFuncs.current[0]
    if (lastAbortController) {
      lastAbortController.abort() // Cancel the request
    }

    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)
    try {
      // Step 1: Check cache before requesting a new document
      const result = await checkCache(templateValue, fieldValues, abortController.signal)
      setTimeout(() => {
        if (result) {
          const date = new Date(result.created * 1000) // Convert to milliseconds
          //setshowWizardCacheDialogMessage('The Last Report of with the chosen parameters was generated at: ' + date.toUTCString());
          setshowWizardCacheDialogMessage(
            `The Last ${templateValue} Report with the chosen parameters was generated at: ` + date.toUTCString()
          )
          switchPageDialogContentPropsWizardCache.title = WizardCacheDialogMessage
          setshowWizardCacheDialog(true)
        } else {
          //add another dialogue
          //setshowNoCache
          setshowWizardNoCacheDialog(true)
        }
      }, 500)
    } catch (e) {
      console.log('Error')
      console.log(e)
      return
    } finally {
      //setMessages([...messages, userMessage]);}
      //setIsLoadingWizard(false);
      //setShowLoadingMessage(false);
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      //setProcessMessages(messageStatus.Done);
      //callbackRef?.current?.(true);
    }
    return abortController.abort()
  }

  const makeApiRequestDocumentWizardCache = async (
    question: ChatMessage['content'],
    source: string,
    sys_message: string,
    tokens: number,
    temperature: number,
    search_web: boolean,
    search_trusted_web: boolean,
    doc_wizard: boolean,
    template: string,
    first_draft: boolean,
    context?: string,
    conversationId?: string
  ) => {
    handleCloseWizardCacheDialog()
    //handleCloseWizardWarningDialog();
    isCancelledRef.current = false
    setShowCopyButton(false)
    setIsLoadingWizard(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: question as string,
      context,
      source,
      sys_message,
      tokens,
      temperature,
      search_web,
      search_trusted_web,
      doc_wizard,
      template,
      first_draft,
      date: new Date().toISOString()
    }

    let conversation: Conversation | null | undefined
    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question as string,
        messages: [userMessage],
        date: new Date().toISOString()
      }
    } else {
      conversation = appStateContext?.state?.currentChat
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoadingWizard(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
      }
    }

    if (isCancelledRef.current) {
      console.log('Process cancelled, skipping update.')
      return
    }
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
    setMessages(conversation.messages)

    const request: ConversationRequest = {
      messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
    }

    try {
      // Step 1: Check cache before requesting a new document
      const result = await checkCache(templateValue, fieldValues, abortController.signal)

      // Use cache if there's any

      if (result.choices?.length > 0) {
        result.choices[0].messages.forEach((msg: { id: any; date: string }) => {
          msg.id = result.id
          msg.date = new Date().toISOString()
        })

        if (result.choices[0].messages?.some((m: { role: string }) => m.role === ASSISTANT)) {
          setShowLoadingMessage(false)
        }

        result.choices[0].messages.forEach((resultObj: ChatMessage) => {
          processResultMessage(resultObj, userMessage, conversationId)
        })

        if (!abortController.signal.aborted && !isCancelledRef.current) {
          conversation.messages.push(toolMessage, assistantMessage)
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
          setMessages([...messages, toolMessage, assistantMessage])
        } else {
          console.log('Request was aborted, skipping state update.')
        }
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = 'An error occurred. Please try again.'
        if (e instanceof Error) {
          errorMessage = e.message
        }

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString()
        }

        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }

        conversation.messages.push(errorChatMsg)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        setMessages([...messages, errorChatMsg])
      } else {
        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }
        setMessages([...messages, userMessage])
      }
    } finally {
      setIsLoadingWizard(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
      callbackRef?.current?.(true)
    }

    return abortController.abort()
  }

  const makeApiRequestDocumentWizardNoCache = async (
    question: ChatMessage['content'],
    source: string,
    sys_message: string,
    tokens: number,
    temperature: number,
    search_web: boolean,
    search_trusted_web: boolean,
    doc_wizard: boolean,
    template: string,
    first_draft: boolean,
    context?: string,
    conversationId?: string
  ) => {
    handleCloseWizardCacheDialog()
    handleCloseWizardNoCacheDialog()
    handleCloseWizardWarningDialog()
    isCancelledRef.current = false
    setShowCopyButton(false)
    setIsLoadingWizard(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: question as string,
      context,
      source,
      sys_message,
      tokens,
      temperature,
      search_web,
      search_trusted_web,
      doc_wizard,
      template,
      first_draft,
      date: new Date().toISOString()
    }

    let conversation: Conversation | null | undefined
    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question as string,
        messages: [userMessage],
        date: new Date().toISOString()
      }
    } else {
      conversation = appStateContext?.state?.currentChat
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoadingWizard(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
      }
    }

    if (isCancelledRef.current) {
      console.log('Process cancelled, skipping update.')
      return
    }
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
    setMessages(conversation.messages)

    const request: ConversationRequest = {
      messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
    }

    try {
      // Step 1: Start wizard request and get task ID
      const taskId = await wizardApi(request, abortController.signal)

      setCurrentTaskId(taskId) // Store the task ID in state

      console.log('Task started, polling for results...')

      // Step 2: Poll for the result (every 10 seconds until it's ready)
      const result = await pollWizardResult(taskId, abortController.signal)

      // Step 3: Process the received result
      if (result.choices?.length > 0) {
        result.choices[0].messages.forEach((msg: { id: any; date: string }) => {
          msg.id = result.id
          msg.date = new Date().toISOString()
        })

        if (result.choices[0].messages?.some((m: { role: string }) => m.role === ASSISTANT)) {
          setShowLoadingMessage(false)
        }

        result.choices[0].messages.forEach((resultObj: ChatMessage) => {
          processResultMessage(resultObj, userMessage, conversationId)
        })

        if (!abortController.signal.aborted && !isCancelledRef.current) {
          conversation.messages.push(toolMessage, assistantMessage)
          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
          setMessages([...messages, toolMessage, assistantMessage])
        } else {
          console.log('Request was aborted, skipping state update.')
        }
      } else if (result.error) {
        throw new Error(result.error)
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = 'An error occurred. Please try again.'
        if (e instanceof Error) {
          errorMessage = e.message
        }

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString()
        }

        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }

        conversation.messages.push(errorChatMsg)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        setMessages([...messages, errorChatMsg])
      } else {
        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }
        setMessages([...messages, userMessage])
      }
    } finally {
      setIsLoadingWizard(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
      callbackRef?.current?.(true)
    }

    return abortController.abort()
  }

  //HS in progress end
  const makeApiRequestDocumentWizard = async (
    question: ChatMessage['content'],
    source: string,
    sys_message: string,
    tokens: number,
    temperature: number,
    search_web: boolean,
    search_trusted_web: boolean,
    doc_wizard: boolean,
    template: string,
    first_draft: boolean,
    context?: string,
    conversationId?: string
  ) => {
    handleCloseWizardWarningDialog()
    isCancelledRef.current = false
    setShowCopyButton(false)
    setIsLoadingWizard(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: question as string,
      context,
      source,
      sys_message,
      tokens,
      temperature,
      search_web,
      search_trusted_web,
      doc_wizard,
      template,
      first_draft,
      date: new Date().toISOString()
    }

    let conversation: Conversation | null | undefined
    if (!conversationId) {
      conversation = {
        id: conversationId ?? uuid(),
        title: question as string,
        messages: [userMessage],
        date: new Date().toISOString()
      }
    } else {
      conversation = appStateContext?.state?.currentChat
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoadingWizard(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
      }
    }

    if (isCancelledRef.current) {
      console.log('Process cancelled, skipping update.')
      return
    }
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
    setMessages(conversation.messages)

    const request: ConversationRequest = {
      messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
    }

    try {
      // Step 1: Check cache before requesting a new document
      const result = await checkCache(templateValue, fieldValues, abortController.signal)

      // Use cache if there's any
      if (result) {
        if (result.choices?.length > 0) {
          result.choices[0].messages.forEach((msg: { id: any; date: string }) => {
            msg.id = result.id
            msg.date = new Date().toISOString()
          })

          if (result.choices[0].messages?.some((m: { role: string }) => m.role === ASSISTANT)) {
            setShowLoadingMessage(false)
          }

          result.choices[0].messages.forEach((resultObj: ChatMessage) => {
            processResultMessage(resultObj, userMessage, conversationId)
          })

          if (!abortController.signal.aborted && !isCancelledRef.current) {
            conversation.messages.push(toolMessage, assistantMessage)
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
            setMessages([...messages, toolMessage, assistantMessage])
          } else {
            console.log('Request was aborted, skipping state update.')
          }
        }
        // If there isn't cache, request to generate a new request
      } else {
        // Step 1: Start wizard request and get task ID
        const taskId = await wizardApi(request, abortController.signal)

        setCurrentTaskId(taskId) // Store the task ID in state

        console.log('Task started, polling for results...')

        // Step 2: Poll for the result (every 10 seconds until it's ready)
        const result = await pollWizardResult(taskId, abortController.signal)

        // Step 3: Process the received result
        if (result.choices?.length > 0) {
          result.choices[0].messages.forEach((msg: { id: any; date: string }) => {
            msg.id = result.id
            msg.date = new Date().toISOString()
          })

          if (result.choices[0].messages?.some((m: { role: string }) => m.role === ASSISTANT)) {
            setShowLoadingMessage(false)
          }

          result.choices[0].messages.forEach((resultObj: ChatMessage) => {
            processResultMessage(resultObj, userMessage, conversationId)
          })

          if (!abortController.signal.aborted && !isCancelledRef.current) {
            conversation.messages.push(toolMessage, assistantMessage)
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
            setMessages([...messages, toolMessage, assistantMessage])
          } else {
            console.log('Request was aborted, skipping state update.')
          }
        } else if (result.error) {
          throw new Error(result.error)
        }
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = 'An error occurred. Please try again.'
        if (e instanceof Error) {
          errorMessage = e.message
        }

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString()
        }

        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }

        conversation.messages.push(errorChatMsg)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation })
        setMessages([...messages, errorChatMsg])
      } else {
        if (isCancelledRef.current) {
          console.log('Process cancelled, skipping update.')
          return
        }
        setMessages([...messages, userMessage])
      }
    } finally {
      setIsLoadingWizard(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
      callbackRef?.current?.(true)
    }

    return abortController.abort()
  }

  const makeApiRequestWithCosmosDB = async (
    question: ChatMessage['content'],
    source: string,
    sys_message: string,
    tokens: number,
    temperature: number,
    search_web: boolean,
    search_trusted_web: boolean,
    doc_wizard: boolean,
    template: string,
    first_draft: boolean,
    context?: string,
    conversationId?: string,
    countryValue?: string
  ) => {
    // Hide copy button until the answer is fully shown
    setShowCopyButton(false)
    setIsLoading(true)
    setShowLoadingMessage(true)
    const abortController = new AbortController()
    abortFuncs.current.unshift(abortController)
    var dateString = ''
    if (filterByYear) {
      selectedLowerYear
      //dateString = ' Between '+ selectedLowerYear + ' and ' + selectedYear +'.'}
      //dateString = 'Only documents which names reference between '+ selectedLowerYear + ' and ' + selectedYear +'.'
      dateString = yearPrompt_fe
        .replace('{startYear}', selectedYear.toString())
        .replace('{endYear}', selectedLowerYear.toString())
      //{startYear}  {country} {endYear}
    }
    var countryString = ''
    if (countryValue != undefined) {
      countryString = countryPrompt_fe.replace('{country}', countryValue)
    }
    const questionContent =
      typeof question === 'string'
        ? countryValue === 'All Countries'
          ? question + dateString
          : question + countryString + dateString
        : [
            { type: 'text', text: question[0].text },
            { type: 'image_url', image_url: { url: question[1].image_url.url } }
          ]
    question = typeof question !== 'string' && question[0]?.text?.length > 0 ? question[0].text : question
    // + 'Consider data between '+ selectedLowerYear + ' and ' + selectedYear +'.'

    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      content: questionContent as string,
      context: context,
      source: source,
      sys_message: sys_message,
      tokens: tokens,
      temperature: temperature,
      search_web: search_web,
      search_trusted_web: search_trusted_web,
      doc_wizard: doc_wizard,
      template: template,
      first_draft: first_draft,
      date: new Date().toISOString()
    }

    let request: ConversationRequest
    let conversation
    if (conversationId) {
      conversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
      if (!conversation) {
        console.error('Conversation not found.')
        setIsLoading(false)
        setShowLoadingMessage(false)
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
        return
      } else {
        conversation.messages.push(userMessage)
        request = {
          messages: [...conversation.messages.filter(answer => answer.role !== ERROR)]
        }
      }
    } else {
      request = {
        messages: [userMessage].filter(answer => answer.role !== ERROR)
      }
      setMessages(request.messages)
    }
    let result = {} as ChatResponse
    var errorResponseMessage = 'Please try again. If the problem persists, please contact the site administrator.'
    try {
      const response = conversationId
        ? await historyGenerate(request, abortController.signal, conversationId)
        : await historyGenerate(request, abortController.signal)
      if (!response?.ok) {
        const responseJson = await response!.json()
        errorResponseMessage =
          responseJson.error === undefined ? errorResponseMessage : parseErrorMessage(responseJson.error)
        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: `There was an error generating a response. Chat history can't be saved at this time. ${errorResponseMessage}`,
          date: new Date().toISOString()
        }
        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation.messages.push(errorChatMsg)
        } else {
          setMessages([...messages, userMessage, errorChatMsg])
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        setMessages([...resultConversation.messages])
        return
      }
      if (response?.body) {
        const reader = response.body.getReader()

        let runningText = ''
        while (true) {
          setProcessMessages(messageStatus.Processing)
          const { done, value } = await reader.read()
          if (done) break

          var text = new TextDecoder('utf-8').decode(value)
          const objects = text.split('\n')
          objects.forEach(obj => {
            try {
              if (obj !== '' && obj !== '{}') {
                runningText += obj
                result = JSON.parse(runningText)
                if (!result.choices?.[0]?.messages?.[0].content) {
                  errorResponseMessage = NO_CONTENT_ERROR
                  throw Error()
                }
                if (result.choices?.length > 0) {
                  result.choices[0].messages.forEach(msg => {
                    msg.id = result.id
                    msg.date = new Date().toISOString()
                  })
                  if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                    setShowLoadingMessage(false)
                  }
                  result.choices[0].messages.forEach(resultObj => {
                    processResultMessage(resultObj, userMessage, conversationId)
                  })
                }
                runningText = ''
              } else if (result.error) {
                throw Error(result.error)
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                console.error(e)
                throw e
              } else {
                console.log('Incomplete message. Continuing...')
              }
            }
          })
        }

        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage)
        } else {
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date
          }
          isEmpty(toolMessage)
            ? resultConversation.messages.push(assistantMessage)
            : resultConversation.messages.push(toolMessage, assistantMessage)
        }
        if (!resultConversation) {
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        isEmpty(toolMessage)
          ? setMessages([...messages, assistantMessage])
          : setMessages([...messages, toolMessage, assistantMessage])
      }
    } catch (e) {
      if (!abortController.signal.aborted) {
        let errorMessage = `An error occurred. ${errorResponseMessage}`
        if (result.error?.message) {
          errorMessage = result.error.message
        } else if (typeof result.error === 'string') {
          errorMessage = result.error
        }

        errorMessage = parseErrorMessage(errorMessage)

        let errorChatMsg: ChatMessage = {
          id: uuid(),
          role: ERROR,
          content: errorMessage,
          date: new Date().toISOString()
        }
        let resultConversation
        if (conversationId) {
          resultConversation = appStateContext?.state?.chatHistory?.find(conv => conv.id === conversationId)
          if (!resultConversation) {
            console.error('Conversation not found.')
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation.messages.push(errorChatMsg)
        } else {
          if (!result.history_metadata) {
            console.error('Error retrieving data.', result)
            let errorChatMsg: ChatMessage = {
              id: uuid(),
              role: ERROR,
              content: errorMessage,
              date: new Date().toISOString()
            }
            setMessages([...messages, userMessage, errorChatMsg])
            setIsLoading(false)
            setShowLoadingMessage(false)
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
            return
          }
          resultConversation = {
            id: result.history_metadata.conversation_id,
            title: result.history_metadata.title,
            messages: [userMessage],
            date: result.history_metadata.date
          }
          resultConversation.messages.push(errorChatMsg)
        }
        if (!resultConversation) {
          setIsLoading(false)
          setShowLoadingMessage(false)
          abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
          return
        }
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation })
        setMessages([...messages, errorChatMsg])
      } else {
        setMessages([...messages, userMessage])
      }
    } finally {
      // Show Copy button
      setIsLoading(false)
      setShowLoadingMessage(false)
      abortFuncs.current = abortFuncs.current.filter(a => a !== abortController)
      setProcessMessages(messageStatus.Done)
      callbackRef?.current?.(true)
    }
    return abortController.abort()
  }

  const clearChat = async () => {
    setFilterByYear(false)
    setClearingChat(true)
    setMessages([]) // Immediately clear UI messages before async call

    if (appStateContext?.state.currentChat?.id && appStateContext?.state.isCosmosDBAvailable.cosmosDB) {
      let response = await historyClear(appStateContext?.state.currentChat.id);
      if (!response!.ok) {
        setErrorMsg({
          title: 'Error clearing current chat',
          subtitle: 'Please try again. If the problem persists, please contact the site administrator.'
        })
        toggleErrorDialog()
      } else {
        appStateContext?.dispatch({
          type: 'DELETE_CURRENT_CHAT_MESSAGES',
          payload: appStateContext?.state.currentChat.id
        })
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext?.state.currentChat })
      }
    }

    setActiveCitation(undefined)
    setIsCitationPanelOpen(false)
    setIsIntentsPanelOpen(false)
    setClearingChat(false)
  }

  const tryGetRaiPrettyError = (errorMessage: string) => {
    try {
      // Using a regex to extract the JSON part that contains "innererror"
      const match = errorMessage.match(/'innererror': ({.*})\}\}/)
      if (match) {
        // Replacing single quotes with double quotes and converting Python-like booleans to JSON booleans
        const fixedJson = match[1]
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false')
        const innerErrorJson = JSON.parse(fixedJson)
        let reason = ''
        // Check if jailbreak content filter is the reason of the error
        const jailbreak = innerErrorJson.content_filter_result.jailbreak
        if (jailbreak.filtered === true) {
          reason = 'Jailbreak'
        }

        // Returning the prettified error message
        if (reason !== '') {
          return (
            'The prompt was filtered due to triggering Azure OpenAI’s content filtering system.\n' +
            'Reason: This prompt contains content flagged as ' +
            reason +
            '\n\n' +
            'Please modify your prompt and retry. Learn more: https://go.microsoft.com/fwlink/?linkid=2198766'
          )
        }
      }
    } catch (e) {
      console.error('Failed to parse the error:', e)
    }
    return errorMessage
  }

  const parseErrorMessage = (errorMessage: string) => {
    let errorCodeMessage = errorMessage.substring(0, errorMessage.indexOf('-') + 1)
    const innerErrorCue = "{\\'error\\': {\\'message\\': "
    if (errorMessage.includes(innerErrorCue)) {
      try {
        let innerErrorString = errorMessage.substring(errorMessage.indexOf(innerErrorCue))
        if (innerErrorString.endsWith("'}}")) {
          innerErrorString = innerErrorString.substring(0, innerErrorString.length - 3)
        }
        innerErrorString = innerErrorString.replaceAll("\\'", "'")
        let newErrorMessage = errorCodeMessage + ' ' + innerErrorString
        errorMessage = newErrorMessage
      } catch (e) {
        console.error('Error parsing inner error message: ', e)
      }
    }

    return tryGetRaiPrettyError(errorMessage)
  }

  const newChat = () => {
    setFilterByYear(false)
    setProcessMessages(messageStatus.Processing)
    setMessages([])
    setIsCitationPanelOpen(false)
    setIsIntentsPanelOpen(false)
    setActiveCitation(undefined)
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    setProcessMessages(messageStatus.Done)
  }

  const stopGenerating = () => {
    abortFuncs.current.forEach(a => a.abort())
    setShowLoadingMessage(false)
    setIsLoading(false)
  }

  const stopGeneratingWizard = async () => {
    isCancelledRef.current = true // Mark as cancelled
    abortFuncs.current.forEach(a => a.abort()) // Abort all ongoing requests
    abortFuncs.current = [] // Clear the abort controllers

    setShowLoadingMessage(false)
    setIsLoadingWizard(false)

    if (currentTaskId) {
      try {
        await fetch(`/wizard/cancel/${currentTaskId}`, { method: 'POST' })
      } catch (err) {
        console.error('Failed to cancel task:', err)
      }
    }

    setCurrentTaskId(null) // Clear task ID
    newChat()
  }

  useEffect(() => {
    
    if (appStateContext?.state.currentChat) {
      setMessages(appStateContext.state.currentChat.messages)
    } else {
      setMessages([])
    }
  }, [appStateContext?.state.currentChat])

  useLayoutEffect(() => {
    const saveToDB = async (messages: ChatMessage[], id: string) => {
      const response = await historyUpdate(messages, id)
      return response
    }

    if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
      if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
        if (!appStateContext?.state.currentChat?.messages) {
          console.error('Failure fetching current chat state.')
          return
        }
        const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

        if (!noContentError) {
          saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
            .then(res => {
              if (!res!.ok) {
                let errorMessage =
                  "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator."
                let errorChatMsg: ChatMessage = {
                  id: uuid(),
                  role: ERROR,
                  content: errorMessage,
                  date: new Date().toISOString()
                }
                if (!appStateContext?.state.currentChat?.messages) {
                  let err: Error = {
                    ...new Error(),
                    message: 'Failure fetching current chat state.'
                  }
                  throw err
                }
                setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
              }
              return res as Response
            })
            .catch(err => {
              console.error('Error: ', err)
              let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500
              }
              return errRes
            })
        }
      } else {
      }
      appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat })
      setMessages(appStateContext.state.currentChat.messages)
      setProcessMessages(messageStatus.NotRunning)
    }
  }, [processMessages])

/*    useEffect(() => {
    if (AUTH_ENABLED !== undefined) getUserInfoList()
  }, [AUTH_ENABLED])  */

  useLayoutEffect(() => {
    chatMessageStreamEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [showLoadingMessage, processMessages])

  const onShowCitation = (citation: Citation) => {
    setActiveCitation(citation)
    setIsCitationPanelOpen(true)
  }

  const onShowExecResult = (answerId: string) => {
    setIsIntentsPanelOpen(true)
  }

  const onViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes('blob.core')) {
      window.open(citation.url, '_blank')
    }
  }

  const parseCitationFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const toolMessage = JSON.parse(message.content) as ToolMessageContent
        return toolMessage.citations
      } catch {
        return []
      }
    }
    return []
  }

  const parsePlotFromMessage = (message: ChatMessage) => {
    if (message?.role && message?.role === 'tool' && typeof message?.content === 'string') {
      try {
        const execResults = JSON.parse(message.content) as AzureSqlServerExecResults
        const codeExecResult = execResults.all_exec_results.at(-1)?.code_exec_result

        if (codeExecResult === undefined) {
          return null
        }
        return codeExecResult.toString()
      } catch {
        return null
      }
      // const execResults = JSON.parse(message.content) as AzureSqlServerExecResults;
      // return execResults.all_exec_results.at(-1)?.code_exec_result;
    }
    return null
  }

  const disabledButton = () => {
    return (
      isLoading ||
      (messages && messages.length === 0) ||
      clearingChat ||
      appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading
    )
  }

  const onYearSliderChange = (_: unknown, range: [number, number] | undefined) => {
    if (range != undefined) {
      setSelectedLowerYear(range[0])
      setSelectedYear(range[1])
      setFilterByYear(true)
    }
  }

  const [selectedTab, setSelectedTab] = useState<'chat' | 'wizard'>('chat')

  const [showFilters, setShowFilters] = useState(false)

  return (
    <div className={styles.container} role="main">
      {showAuthMessage ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: 'darkorange', height: '200px', width: '200px' }}
          />
          <h1 className={styles.chatEmptyStateTitle}>Authentication Not Configured</h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an identity provider by finding your app in the{' '}
            <a href="https://portal.azure.com/" target="_blank">
              Azure Portal
            </a>
            and following{' '}
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank">
              these instructions
            </a>
            .
          </h2>
          <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
            <strong>Authentication configuration takes a few minutes to apply. </strong>
          </h2>
          <h2 className={styles.chatEmptyStateSubtitle} style={{ fontSize: '20px' }}>
            <strong>If you deployed in the last 10 minutes, please wait and reload the page after 10 minutes.</strong>
          </h2>
        </Stack>
      ) : (
        <>
          <Stack horizontal className={styles.chatRoot}>
            <div className={styles.chatContainer}>
              <div className="flex items-center flex-wrap">
                <Button
                  onClick={clickTab1}
                  className={`min-w-[70px] flex-1 rounded-none first:rounded-l-md last:rounded-r-md bg-white hover:bg-slate-100 border-t-4 
                              ${isTab1Open ? 'text-pink-500 border-pink-500' : 'text-gray-500 border-transparent'}`}>
                  <MessageCircle className={`mr-1 ${isTab1Open ? 'text-pink-500' : 'text-gray-400'}`} />
                  Chat
                </Button>

                <Button
                  onClick={clickTab2}
                  className={`min-w-[70px] flex-1 rounded-none first:rounded-l-md last:rounded-r-md bg-white hover:bg-slate-100 border-t-4 
                              ${isTab3Open ? 'text-lime-500 border-lime-500' : 'text-gray-500 border-transparent'}`}>
                  <WandSparkles className={`mr-1 ${isTab3Open ? 'text-lime-500' : 'text-gray-400'}`} />
                  Wizard
                </Button>
              </div>
              {!messages || messages.length < 1 ? (
                <Stack className={styles.chatEmptyState}>
                  {/* <img src={logo} className={styles.chatIcon} aria-hidden="true" /> */}
                  <h1 className="font-bold text-3xl pt-8 text-center font-[montserrat]">
                    {docWizard ? 'What document do you want to create?' : 'Hello, there! How can I help?'}
                  </h1>
                  <h2 className={styles.chatEmptyStateSubtitle}>
                    {docWizard ? ui?.wizard_description : ui?.chat_description}
                  </h2>
                  {docWizard && <h3 className={styles.wizardEmptyStateSubtitle}>Select the template type.</h3>}
                  {docWizard && (
                    <>
                      <div className="flex gap-2 w-full py-4">
                        {templateOptions.map(template => {
                          const Icon = templateIcons[template.key] || File

                          return (
                            <Button
                              key={template.key}
                              onClick={() => onTemplateValueChange(template)}
                              className={`flex-1 px-2 flex items-center justify-between gap-2 w-48 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150`}>
                              <span className="flex items-center gap-2">
                                <Icon
                                  className={`h-5 w-5 transition-colors duration-200 ${
                                    templateValue === template.key ? 'text-sky-500' : 'text-gray-500'
                                  }`}
                                />
                                <span>{template.text}</span>
                              </span>
                            </Button>
                          )
                        })}
                      </div>

                      {templateValue && (
                        <>
                          <hr className="mt-3 mb-3 h-px border-t-0 bg-transparent bg-gradient-to-r from-transparent via-neutral-500 to-transparent opacity-25 dark:via-neutral-400" />
                          <span className="text-sm text-gray-500 mb-3">Template settings</span>
                          <div className="flex gap-2 w-full">
                            {selectedTemplateFields.map(field => (
                              <div key={templateValue + field.name} className="flex-1 px-2">
                                <AppDropdown
                                  placeholder={`Select ${field.name}`}
                                  options={field.options.map(opt => ({ key: opt, text: opt }))}
                                  onChange={(event, option) => onFieldValueChange(field.name, option)}
                                />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      <StackItem style={{ width: '100%', marginTop: 20 }}>
                        {templateValue !== 'Choose a template' && isAllFieldsSelected && (
                          <div className="flex justify-center w-full">
                            <Button
                              onClick={showDialogDocumentWizard}
                              className="flex items-center justify-center gap-2 px-4 py-2 rounded-2xl border border-gray-300 bg-[#cd0756] text-sm text-white shadow-sm hover:bg-[#b0064c] focus:ring-2 focus:ring-pink-200 transition-all duration-150">
                              Draft Report
                            </Button>
                          </div>
                        )}
                      </StackItem>
                    </>
                  )}
                  {!docWizard && (
                    <div className="flex gap-2 w-full py-4">
                      <div className="flex-1 px-2">
                        <AppDropdown
                          placeholder="Source"
                          options={sourceOptions}
                          onChange={(event, option) => onSourceChange(option)}
                          disabled={searchWeb || searchTrustedWeb}
                        />
                      </div>
                      <span className="h-9 w-px bg-gray-300" />
                      <div className="flex-1 px-2">
                        <Button
                          onClick={clickWebSearch}
                          disabled={searchTrustedWeb}
                          className="flex items-center justify-between gap-2 w-48 px-3 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150">
                          <span className="flex items-center gap-2 cursor-pointer">
                            <Globe
                              className={`h-5 w-5 transition-colors duration-200 ${
                                searchWeb ? 'text-lime-500' : 'text-gray-500'
                              }`}
                            />
                            <span>Search the web</span>
                          </span>
                        </Button>
                      </div>
                      <div className="flex-1 px-2">
                        <Button
                          onClick={clickTrustedWebSearch}
                          disabled={searchWeb}
                          className="flex items-center justify-between gap-2 w-48 px-3 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150">
                          <span className="flex items-center gap-2 cursor-pointer">
                            <Share2
                              className={`h-5 w-5 transition-colors duration-200 ${
                                searchTrustedWeb ? 'text-pink-950' : 'text-gray-500'
                              }`}
                            />
                            <span>ETF trusted network</span>
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </Stack>
              ) : (
                <div
                  className={`scrollbar-hide ${styles.chatMessageStream}`}
                  style={{ marginBottom: isLoading ? '40px' : '0px' }}
                  role="log">
                  {messages.map((answer, index) => (
                    <>
                      {answer.role === 'user' ? (
                        <div className={styles.chatMessageUser} tabIndex={0}>
                          <div className={styles.chatMessageUserMessage}>
                            {typeof answer.content === 'string' && answer.content ? (
                              answer.content
                            ) : Array.isArray(answer.content) ? (
                              <>
                                {answer.content[0].text}{' '}
                                <img
                                  className={styles.uploadedImageChat}
                                  src={answer.content[1].image_url.url}
                                  alt="Uploaded Preview"
                                />
                              </>
                            ) : null}
                          </div>
                        </div>
                      ) : answer.role === 'assistant' ? (
                        <div className={styles.chatMessageGpt}>
                          {typeof answer.content === 'string' && (
                            <Answer
                              answer={{
                                answer: answer.content,
                                citations: parseCitationFromMessage(messages[index - 1]),
                                generated_chart: parsePlotFromMessage(messages[index - 1]),
                                message_id: answer.id,
                                feedback: answer.feedback,
                                exec_results: execResults
                              }}
                              onCitationClicked={c => onShowCitation(c)}
                              onExectResultClicked={() => onShowExecResult(answerId)}
                              onRef={handleOnRef}
                              docWizard={docWizard}
                              copyButton={showCopyButton}
                              template={templateValue === 'Choose a template' ? undefined : templateValue}
                            />
                          )}
                        </div>
                      ) : answer.role === ERROR ? (
                        <div className={styles.chatMessageError}>
                          <Stack horizontal className={styles.chatMessageErrorContent}>
                            <ErrorCircleRegular
                              className={styles.errorIcon}
                              style={{ color: 'rgba(182, 52, 67, 1)' }}
                            />
                            <span>Error</span>
                          </Stack>
                          <span className={styles.chatMessageErrorContent}>
                            {typeof answer.content === 'string' && answer.content}
                          </span>
                        </div>
                      ) : null}
                    </>
                  ))}
                  {showLoadingMessage && (
                    <>
                      <div className={styles.chatMessageGpt}>
                        <Answer
                          answer={{
                            answer: 'Generating answer...',
                            citations: [],
                            generated_chart: null
                          }}
                          onCitationClicked={() => null}
                          onExectResultClicked={() => null}
                          onRef={handleOnRef}
                          docWizard={docWizard}
                          copyButton={showCopyButton}
                        />
                      </div>
                    </>
                  )}
                  <div ref={chatMessageStreamEnd} />
                </div>
              )}

              <Stack horizontal className={styles.chatInput}>
                {isLoadingWizard && messages.length > 0 && (
                  <Stack
                    horizontal
                    className={styles.stopGeneratingContainer}
                    role="button"
                    aria-label="Stop generating"
                    tabIndex={0}
                    onClick={stopGeneratingWizard}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGeneratingWizard() : null)}>
                    <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
                    <span className={styles.stopGeneratingText} aria-hidden="true">
                      Stop generating
                    </span>
                  </Stack>
                )}
                {isLoading && messages.length > 0 && (
                  <Stack
                    horizontal
                    className={styles.stopGeneratingContainer}
                    role="button"
                    aria-label="Stop generating"
                    tabIndex={0}
                    onClick={stopGenerating}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? stopGenerating() : null)}>
                    <SquareRegular className={styles.stopGeneratingIcon} aria-hidden="true" />
                    <span className={styles.stopGeneratingText} aria-hidden="true">
                      Stop generating
                    </span>
                  </Stack>
                )}
                {/* <Stack>
                  {!docWizard &&
                    messages.length >= 1 &&
                    appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && (
                      <CommandBarButton
                        role="button"
                        styles={{
                          icon: {
                            color: '#FFFFFF'
                          },
                          iconDisabled: {
                            color: '#BDBDBD !important'
                          },
                          root: {
                            color: '#FFFFFF',
                            background:
                              'radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)'
                          },
                          rootDisabled: {
                            background: '#F0F0F0'
                          }
                        }}
                        title="New Chat"
                        className={styles.newChatIcon}
                        iconProps={{ iconName: 'Add' }}
                        onClick={newChat}
                        disabled={disabledButton()}
                        aria-label="start a new chat button"
                      />
                    )}
                  {!docWizard && messages.length >= 1 && (
                    <CommandBarButton
                      role="button"
                      styles={{
                        icon: {
                          color: '#FFFFFF'
                        },
                        iconDisabled: {
                          color: '#BDBDBD !important'
                        },
                        root: {
                          color: '#FFFFFF',
                          background:
                            'radial-gradient(109.81% 107.82% at 100.1% 90.19%, #0F6CBD 33.63%, #2D87C3 70.31%, #8DDDD8 100%)'
                        },
                        rootDisabled: {
                          background: '#F0F0F0'
                        }
                      }}
                      title="Delete Chat"
                      className={
                        appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
                          ? styles.clearChatBroom
                          : styles.clearChatBroomNoCosmos
                      }
                      iconProps={{ iconName: 'Broom' }}
                      onClick={
                        appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
                          ? clearChat
                          : newChat
                      }
                      disabled={disabledButton()}
                      aria-label="clear chat button"
                    />
                  )}
                  <Dialog
                    hidden={hideErrorDialog}
                    onDismiss={handleErrorDialogClose}
                    dialogContentProps={errorDialogContentProps}
                    modalProps={modalProps}></Dialog>
                </Stack> */}
                {(!docWizard || (docWizard && messages.length > 1)) && (
                  <QuestionInput
                    clearOnSend
                    placeholder="Message Chatty"
                    disabled={isLoading}
                    messagesLength={messages.length}
                    onSend={({ question, context, id }) => {
                      appStateContext?.state.isCosmosDBAvailable?.cosmosDB && !docWizard
                        ? makeApiRequestWithCosmosDB(
                            question,
                            sourceValue,
                            sysMessage,
                            tokenValue,
                            temperatureValue,
                            searchWeb,
                            searchTrustedWeb,
                            docWizard,
                            templateValue,
                            docWizard && messages.length < 1,
                            context,
                            id,
                            countryValue
                          )
                        : makeApiRequestWithoutCosmosDB(
                            question,
                            sourceValue,
                            sysMessage,
                            tokenValue,
                            temperatureValue,
                            searchWeb,
                            searchTrustedWeb,
                            docWizard,
                            templateValue,
                            docWizard && messages.length < 1,
                            context,
                            id,
                            countryValue
                          )
                    }}
                    conversationId={
                      appStateContext?.state.currentChat?.id ? appStateContext?.state.currentChat?.id : undefined
                    }
                  />
                )}
              </Stack>
              {/* Additional Filters Collapsible Panel */}
              {(!docWizard || (docWizard && messages.length > 1)) && (
                <div className="w-full px-4 pb-2">
                  <div className="w-full max-w-[900px] mx-auto">
                    {/* Header */}
                    <div
                      onClick={() => setShowFilters(prev => !prev)}
                      className="flex items-center gap-3 cursor-pointer xl px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-700">Additional filters</span>
                      </div>

                      {/* Arrow */}
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${
                          showFilters ? 'rotate-180' : 'rotate-0'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {/* Collapsible Content */}
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        showFilters ? 'max-h-40 mt-2' : 'max-h-0'
                      }`}>
                      <div className="flex gap-2 w-full">
                        <div className="flex-1 px-2 ml-2 mb-2">
                          {/* Label */}
                          <label className="block text-sm pb-2 text-gray-500 mb-1">Countries</label>

                          <AppDropdown
                            placeholder="Select country"
                            options={countryOptions}
                            onChange={(event, option) => onCountryChange(option)}
                            defaultValue={countryValue}
                          />
                        </div>

                        <Slider
                          label="Time range"
                          //min={2010}
                          //max={2025}
                          min={startYear}
                          max={endYear}
                          step={1}
                          ranged
                          value={selectedYear}
                          lowerValue={selectedLowerYear}
                          showValue
                          onChange={onYearSliderChange}
                          //styles={{ root: { width: 300 } }}
                          styles={sliderStyles}
                          disabled={startYear === -1 || endYear === -1}
                        />
                      </div>
                      {messages.length > 1 && (
                        <div className="flex gap-2 w-full py-4 pl-2">
                          <div className="px-2">
                            <AppDropdown
                              placeholder="Source"
                              options={sourceOptions}
                              onChange={(event, option) => onSourceChange(option)}
                              disabled={searchWeb || searchTrustedWeb}
                            />
                          </div>
                          <span className="h-9 w-px bg-gray-300" />
                          <div className="px-2">
                            <Button
                              onClick={clickWebSearch}
                              disabled={searchTrustedWeb}
                              className="flex items-center justify-between gap-2 w-48 px-3 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150">
                              <span className="flex items-center gap-2 cursor-pointer">
                                <Globe
                                  className={`h-5 w-5 transition-colors duration-200 ${
                                    searchWeb ? 'text-lime-500' : 'text-gray-500'
                                  }`}
                                />
                                <span>Search the web</span>
                              </span>
                            </Button>
                          </div>
                          <div className="px-2">
                            <Button
                              onClick={clickTrustedWebSearch}
                              disabled={searchWeb}
                              className="flex items-center justify-between gap-2 w-48 px-3 py-2 rounded-2xl border border-gray-300 bg-white text-sm text-gray-500 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-200 transition-all duration-150">
                              <span className="flex items-center gap-2 cursor-pointer">
                                <Share2
                                  className={`h-5 w-5 transition-colors duration-200 ${
                                    searchTrustedWeb ? 'text-pink-950' : 'text-gray-500'
                                  }`}
                                />
                                <span>ETF trusted network</span>
                              </span>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {messages.length < 1 && (!docWizard || (docWizard && messages.length > 1)) && (
                <h3 className={styles.chatEmptyStateAIContentWarning}>
                  Chatty can make mistakes. Consider checking important information.
                </h3>
              )}
            </div>
            {/* Citation Panel */}
            {messages && messages.length > 0 && isCitationPanelOpen && activeCitation && (
              <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
                <Stack
                  aria-label="Citations Panel Header Container"
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center">
                  <span aria-label="Citations" className={styles.citationPanelHeader}>
                    Citations
                  </span>
                  <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    aria-label="Close citations panel"
                    onClick={() => setIsCitationPanelOpen(false)}
                  />
                </Stack>
                <h5
                  className={styles.citationPanelTitle}
                  tabIndex={0}
                  title={
                    activeCitation.url && !activeCitation.url.includes('blob.core')
                      ? activeCitation.url
                      : (activeCitation.title ?? '')
                  }
                  onClick={() => onViewSource(activeCitation)}>
                  {activeCitation.title}
                </h5>
                <div tabIndex={0}>
                  <ReactMarkdown
                    linkTarget="_blank"
                    className={styles.citationPanelContent}
                    children={DOMPurify.sanitize(activeCitation.content, { ALLOWED_TAGS: XSSAllowTags })}
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  />
                </div>
              </Stack.Item>
            )}
            {messages && messages.length > 0 && isIntentsPanelOpen && (
              <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Intents Panel">
                <Stack
                  aria-label="Intents Panel Header Container"
                  horizontal
                  className={styles.citationPanelHeaderContainer}
                  horizontalAlign="space-between"
                  verticalAlign="center">
                  <span aria-label="Intents" className={styles.citationPanelHeader}>
                    Intents
                  </span>
                  <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    aria-label="Close intents panel"
                    onClick={() => setIsIntentsPanelOpen(false)}
                  />
                </Stack>
                <Stack horizontalAlign="space-between">
                  {appStateContext?.state?.answerExecResult[answerId]?.map((execResult: ExecResults, index) => (
                    <Stack className={styles.exectResultList} verticalAlign="space-between">
                      <>
                        <span>Intent:</span> <p>{execResult.intent}</p>
                      </>
                      {execResult.search_query && (
                        <>
                          <span>Search Query:</span>
                          <SyntaxHighlighter
                            style={nord}
                            wrapLines={true}
                            lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                            language="sql"
                            PreTag="p">
                            {execResult.search_query}
                          </SyntaxHighlighter>
                        </>
                      )}
                      {execResult.search_result && (
                        <>
                          <span>Search Result:</span> <p>{execResult.search_result}</p>
                        </>
                      )}
                      {execResult.code_generated && (
                        <>
                          <span>Code Generated:</span>
                          <SyntaxHighlighter
                            style={nord}
                            wrapLines={true}
                            lineProps={{ style: { wordBreak: 'break-all', whiteSpace: 'pre-wrap' } }}
                            language="python"
                            PreTag="p">
                            {execResult.code_generated}
                          </SyntaxHighlighter>
                        </>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Stack.Item>
            )}
          </Stack>

          <Dialog
            hidden={!showSwitchingPageDialog}
            onDismiss={handleCloseSwitchingPageDialog}
            dialogContentProps={switchPageDialogContentProps}
            modalProps={modalProps}>
            <DialogFooter>
              <PrimaryButton onClick={switchToTab1} text="Exit" />

              <DefaultButton onClick={handleCloseSwitchingPageDialog} text={'Cancel'} />
            </DialogFooter>
          </Dialog>
          {/**here HS */}
          <Dialog
            hidden={!showWizardCacheDialog}
            onDismiss={handleCloseWizardCacheDialog}
            dialogContentProps={switchPageDialogContentPropsWizardCache}
            modalProps={modalPropsWizard}>
            <DialogFooter>
              <PrimaryButton
                onClick={() =>
                  makeApiRequestDocumentWizardCache(
                    `Generate me a ${templateValue} document`,
                    sourceValue,
                    sysMessage,
                    tokenValue,
                    temperatureValue,
                    searchWeb,
                    searchTrustedWeb,
                    docWizard,
                    templateValue,
                    docWizard && messages.length < 1,
                    JSON.stringify(fieldValues),
                    undefined
                  )
                }
                text="Use Last Draft"
                styles={{
                  root: {
                    height: '45px',
                    minWidth: '75px',
                    fontSize: '14px',
                    padding: '0 20px'
                  }
                }}
              />
              <PrimaryButton
                onClick={() =>
                  makeApiRequestDocumentWizardNoCache(
                    `Generate me a ${templateValue} document`,
                    sourceValue,
                    sysMessage,
                    tokenValue,
                    temperatureValue,
                    searchWeb,
                    searchTrustedWeb,
                    docWizard,
                    templateValue,
                    docWizard && messages.length < 1,
                    JSON.stringify(fieldValues),
                    undefined
                  )
                }
                text="Generate Another Version"
                styles={{
                  root: {
                    height: '45px',
                    minWidth: '75px',
                    fontSize: '14px',
                    padding: '0 20px'
                  }
                }}
              />
              <DefaultButton
                onClick={handleCloseWizardCacheDialog}
                text={'Cancel'}
                styles={{
                  root: {
                    height: '45px',
                    minWidth: '75px',
                    fontSize: '14px',
                    padding: '0 20px'
                  }
                }}
              />
            </DialogFooter>
          </Dialog>

          <Dialog
            hidden={!showWizardNoCacheDialog}
            onDismiss={handleCloseWizardNoCacheDialog}
            dialogContentProps={switchPageDialogContentPropsWizardNoCache}
            modalProps={modalPropsWizardNoCache}>
            <DialogFooter>
              <PrimaryButton
                onClick={() =>
                  makeApiRequestDocumentWizardNoCache(
                    `Generate me a ${templateValue} document`,
                    sourceValue,
                    sysMessage,
                    tokenValue,
                    temperatureValue,
                    searchWeb,
                    searchTrustedWeb,
                    docWizard,
                    templateValue,
                    docWizard && messages.length < 1,
                    JSON.stringify(fieldValues),
                    undefined
                  )
                }
                text="Generate Document"
              />
              <DefaultButton onClick={handleCloseWizardNoCacheDialog} text={'Cancel'} />
            </DialogFooter>
          </Dialog>
          {/* <Dialog
            hidden={!showWizardWarningDialog}
            onDismiss={handleCloseWizardWarningDialog}
            dialogContentProps={wizardWarningProps}
            modalProps={modalProps}>
            <DialogFooter>
              <PrimaryButton onClick={() => makeApiRequestDocumentWizard(
                question,
                sourceValue,
                sysMessage,
                tokenValue,
                temperatureValue,
                searchWeb,
                searchTrustedWeb,
                docWizard,
                templateValue,
                docWizard && messages.length < 1,
                undefined
              )} text="OK" />
              <DefaultButton
                onClick={handleCloseWizardWarningDialog}
                text='Cancel' />
            </DialogFooter>
          </Dialog> */}
        </>
      )}
    </div>
  )
}

export default Chat
